import {
  RadioGroup,
  RadioTile,
  ValidationText,
} from '@utilitywarehouse/hearth-react';
import { useFormikContext } from 'formik';
import { FC } from 'react';

import { EnergyFuelType } from '@generated/gql/graphql';
import { useFieldProps } from '@hooks/useFieldProps';
import { EnergyDetailsFormValues } from '@src/types/energy';
import { BooleanSelection } from '@src/types/index';

import './styles.scss';

interface PrepaymentFuelRadioFieldProps {
  disabled: boolean;
}

const FIELD_NAME = 'prepaymentFuelType';

const PrepaymentFuelRadioField: FC<PrepaymentFuelRadioFieldProps> = ({
  disabled,
}) => {
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    touched,
    submitCount,
    setFieldValue,
  } = useFormikContext<EnergyDetailsFormValues>();

  const getFieldProps = useFieldProps<EnergyDetailsFormValues>({
    errors,
    values,
    handleBlur,
    handleChange,
    touched,
    submitCount,
  });

  const hasPPM = values.hasPrepayment === BooleanSelection.Yes;
  const isDualFuel = values.fuelType === EnergyFuelType.DualFuel;

  if (!hasPPM || (hasPPM && !isDualFuel)) {
    return null;
  }

  const error = errors[FIELD_NAME];
  const isTouched = touched[FIELD_NAME];
  const hasError = Boolean(error && isTouched);

  return (
    <div className="energyDetailsForm__prepaymentFuelRadioField">
      <RadioGroup
        {...getFieldProps(FIELD_NAME)}
        label="Which of your energy services uses a prepayment meter?"
        disabled={disabled}
        onValueChange={(val: string) => setFieldValue(FIELD_NAME, val)}
      >
        <RadioTile
          value={EnergyFuelType.DualFuel}
          label="Electricity and gas"
          data-testid="prepayment-fuel-type-dual"
          id="prepayment-fuel-type-dual"
        />
        <RadioTile
          value={EnergyFuelType.ElectricityOnly}
          label="Electricity only"
          data-testid="prepayment-fuel-type-electricity"
          id="prepayment-fuel-type-electricity"
        />
        <RadioTile
          value={EnergyFuelType.GasOnly}
          label="Gas only"
          data-testid="prepayment-fuel-type-gas"
          id="prepayment-fuel-type-gas"
        />
      </RadioGroup>
      {hasError ? (
        <ValidationText status="invalid">{error}</ValidationText>
      ) : (
        <span className="energyDetailsForm__errorPlaceholder" />
      )}
    </div>
  );
};

export { PrepaymentFuelRadioField };
